import React, { useEffect, useRef, useState } from 'react';
import { fetchPhotoBlob } from './caseReviewApi';

// Renders a case photo by fetching it as a Blob through ApiClient (so the JWT
// travels in the Authorization header, not in the URL) and displaying an object
// URL. The thumbnail blob is only fetched once the tile scrolls near the
// viewport (IntersectionObserver) so a case with many photos doesn't fire all
// requests at once. Clicking opens the full-resolution image in a new tab.
export default function AuthImage({ caseId, section, filename, label, className = '' }) {
    const [thumbUrl, setThumbUrl] = useState(null);
    const [failed, setFailed] = useState(false);
    const [opening, setOpening] = useState(false);
    const [visible, setVisible] = useState(false);
    const containerRef = useRef(null);
    const objectUrlRef = useRef(null);

    // Defer loading until the tile is near the viewport.
    useEffect(() => {
        if (visible) return undefined;
        const el = containerRef.current;
        if (!el) return undefined;
        if (typeof IntersectionObserver === 'undefined') {
            setVisible(true);
            return undefined;
        }
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries.some((e) => e.isIntersecting)) {
                    setVisible(true);
                    observer.disconnect();
                }
            },
            { rootMargin: '200px' }
        );
        observer.observe(el);
        return () => observer.disconnect();
    }, [visible]);

    // Fetch the thumbnail blob once visible.
    useEffect(() => {
        if (!visible) return undefined;
        let cancelled = false;
        setThumbUrl(null);
        setFailed(false);

        fetchPhotoBlob(caseId, section, filename, { thumb: true })
            .then((blob) => {
                if (cancelled) return;
                const url = URL.createObjectURL(blob);
                objectUrlRef.current = url;
                setThumbUrl(url);
            })
            .catch(() => {
                if (!cancelled) setFailed(true);
            });

        return () => {
            cancelled = true;
            if (objectUrlRef.current) {
                URL.revokeObjectURL(objectUrlRef.current);
                objectUrlRef.current = null;
            }
        };
    }, [visible, caseId, section, filename]);

    const openFullSize = async () => {
        setOpening(true);
        try {
            const blob = await fetchPhotoBlob(caseId, section, filename);
            const url = URL.createObjectURL(blob);
            window.open(url, '_blank', 'noopener,noreferrer');
            // Give the new tab time to load before releasing the blob.
            setTimeout(() => URL.revokeObjectURL(url), 60000);
        } catch (e) {
            // ignore — thumbnail still visible
        } finally {
            setOpening(false);
        }
    };

    return (
        <div ref={containerRef} className={className}>
            {failed ? (
                <div className="w-full h-full flex items-center justify-center bg-gray-100 text-gray-400 text-xs">
                    Unavailable
                </div>
            ) : !thumbUrl ? (
                <div className="w-full h-full bg-gray-100 animate-pulse" aria-label="Loading photo" />
            ) : (
                <img
                    src={thumbUrl}
                    alt={label || filename}
                    onClick={openFullSize}
                    className={`w-full h-full object-cover cursor-pointer hover:opacity-75 transition-opacity duration-200 ${opening ? 'opacity-50' : ''}`}
                />
            )}
        </div>
    );
}
